import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";

const OPT_TODOS_OPERADORES  = { value: "all", label: "Todos los operadores" };
const OPT_TODAS_ZONAS       = { value: "all", label: "Todas las zonas" };
const OPT_TODAS_TECNOLOGIAS = { value: "all", label: "Todas las tecnologías" };

const toId = (v) => {
  if (v == null || v === "" || v === "all") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

export default function useFiltrosCalidad({
  operators = [],
  selectedOperador = null,
  selectedZona = null,
  selectedTec = null,
  disabled = {},
} = {}) {
  const [zonasPorOperador,   setZonasPorOperador]   = useState([]);
  const [tecnologiasPorZona, setTecnologiasPorZona] = useState([]);

  useEffect(() => {
    let vivo = true;
    api.getZonasPorOperador()
      .then((res) => { if (vivo) setZonasPorOperador(Array.isArray(res.data) ? res.data : []); })
      .catch(() => {});
    api.getTecnologiasPorZona()
      .then((res) => { if (vivo) setTecnologiasPorZona(Array.isArray(res.data) ? res.data : []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const catalogo = useMemo(() => {
    const zonasDeOperador = new Map();
    const tecsDeZona      = new Map();
    const nombreZona      = new Map();
    const nombreTec       = new Map();

    zonasPorOperador.forEach((op) => {
      const zonas = new Set();
      (op.zonas ?? []).forEach((z) => {
        zonas.add(z.id_zona);
        if (z.nombre_zona) nombreZona.set(z.id_zona, z.nombre_zona);
      });
      zonasDeOperador.set(op.id_operador, zonas);
    });

    tecnologiasPorZona.forEach((z) => {
      const tecs = new Set();
      (z.tecnologias ?? []).forEach((t) => {
        tecs.add(t.id_tipo_tecnologia);
        if (t.nombre_enlace) nombreTec.set(t.id_tipo_tecnologia, t.nombre_enlace);
      });
      tecsDeZona.set(z.id_zona, tecs);
      if (z.nombre_zona && !nombreZona.has(z.id_zona)) nombreZona.set(z.id_zona, z.nombre_zona);
    });

    const zonas = Array.from(new Set([...nombreZona.keys(), ...tecsDeZona.keys()]))
      .sort((a, b) => a - b);

    return { zonasDeOperador, tecsDeZona, nombreZona, nombreTec, zonas };
  }, [zonasPorOperador, tecnologiasPorZona]);

  const cargado = catalogo.zonas.length > 0;

  const opSel   = disabled.operador ? null : toId(selectedOperador);
  const zonaSel = disabled.zona     ? null : toId(selectedZona);
  const tecSel  = disabled.tec      ? null : toId(selectedTec);

  const zonaOpts = useMemo(() => {
    if (!cargado) return [OPT_TODAS_ZONAS];
    const delOperador = opSel != null ? catalogo.zonasDeOperador.get(opSel) : null;
    const zonas = catalogo.zonas.filter((z) => {
      if (opSel != null && !(delOperador?.has(z))) return false;
      if (tecSel != null && !(catalogo.tecsDeZona.get(z)?.has(tecSel))) return false;
      return true;
    });
    return [
      OPT_TODAS_ZONAS,
      ...zonas.map((z) => ({ value: String(z), label: catalogo.nombreZona.get(z) ?? `Zona ${z}` })),
    ];
  }, [catalogo, cargado, opSel, tecSel]);

  const tecnologiaOpts = useMemo(() => {
    if (!cargado) return [OPT_TODAS_TECNOLOGIAS];
    const delOperador = opSel != null ? catalogo.zonasDeOperador.get(opSel) : null;
    const tecs = new Set();
    catalogo.zonas.forEach((z) => {
      if (zonaSel != null && z !== zonaSel) return;
      if (opSel != null && !(delOperador?.has(z))) return;
      (catalogo.tecsDeZona.get(z) ?? new Set()).forEach((t) => tecs.add(t));
    });
    return [
      OPT_TODAS_TECNOLOGIAS,
      ...Array.from(tecs)
        .sort((a, b) => a - b)
        .map((t) => ({ value: String(t), label: catalogo.nombreTec.get(t) ?? `Tec ${t}` })),
    ];
  }, [catalogo, cargado, opSel, zonaSel]);

  const operadorOpts = useMemo(() => {
    const todos = operators.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador }));
    if (!cargado) return [OPT_TODOS_OPERADORES, ...todos];
    const permitidos = operators.filter((o) => {
      const zonas = catalogo.zonasDeOperador.get(o.id_operador);
      if (!zonas || zonas.size === 0) return false;
      return Array.from(zonas).some((z) => {
        if (zonaSel != null && z !== zonaSel) return false;
        if (tecSel != null && !(catalogo.tecsDeZona.get(z)?.has(tecSel))) return false;
        return true;
      });
    });
    return [
      OPT_TODOS_OPERADORES,
      ...permitidos.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador })),
    ];
  }, [catalogo, cargado, operators, zonaSel, tecSel]);

  const tecnologias = useMemo(
    () => Array.from(catalogo.nombreTec.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.id - b.id),
    [catalogo],
  );

  const zonaLabel = useMemo(
    () => (id) => catalogo.nombreZona.get(Number(id)) ?? `Zona ${id}`,
    [catalogo],
  );

  const tecnologiaLabel = useMemo(
    () => (id) => {
      const nombre = catalogo.nombreTec.get(Number(id));
      if (nombre) return nombre;
      return /^\d+$/.test(String(id)) ? `Tec ${id}` : String(id);
    },
    [catalogo],
  );

  return { operadorOpts, zonaOpts, tecnologiaOpts, tecnologias, zonaLabel, tecnologiaLabel, cargado };
}
